/**
 * AI Context Provider
 * Provides the AI chat state to all components in the app.
 * Rendered at the App level to persist across route changes.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useAIChat, type ChatMessageDisplay, type PendingConfirmation } from '../../hooks/useAIChat';

interface AIContextType {
  // State
  messages: ChatMessageDisplay[];
  isProcessing: boolean;
  pendingConfirmation: PendingConfirmation | null;
  isOpen: boolean;
  error: string | null;
  editingMessageId: string | null;

  // Actions
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
  toggleOpen: () => void;
  openChat: () => void;
  closeChat: () => void;
  confirmAction: () => Promise<void>;
  cancelAction: () => void;
  startEditingMessage: (messageId: string) => void;
  cancelEditing: () => void;
  editMessageAndContinue: (messageId: string, newContent: string) => Promise<void>;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

export function useAIContext() {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAIContext must be used within an AIProvider');
  }
  return context;
}

interface AIProviderProps {
  children: ReactNode;
}

export const AIProvider: React.FC<AIProviderProps> = ({ children }) => {
  const chat = useAIChat();

  return (
    <AIContext.Provider value={{
      ...chat,
      editingMessageId: chat.editingMessageId ?? null,
      startEditingMessage: chat.startEditingMessage ?? (() => {}),
      cancelEditing: chat.cancelEditing ?? (() => {}),
      editMessageAndContinue: chat.editMessageAndContinue ?? (async () => {}),
    }}>
      {children}
    </AIContext.Provider>
  );
};
